import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { usePublicSettings } from '../../hooks/usePublicSettings'
import LoadingSpinner from '../common/LoadingSpinner'
import { AuthLayout, ErrorNote, SubmitButton, INPUT, LINK } from './AuthLayout'

/**
 * Reset password from the email link (/reset-password?token=…). The link is checked first, so an
 * expired or used link says so before the customer types anything. Rules: security.password_min_length.
 */
export default function ResetPassword() {
  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const { checkResetLink, resetPassword, setSessionNotice } = useAuth()
  const navigate = useNavigate()
  const settings = usePublicSettings()
  const minPassword = settings?.['security.password_min_length']

  const [link, setLink] = useState({ checking: true, valid: false, error: null })
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    if (!token) { setLink({ checking: false, valid: false, error: 'This reset link is not complete. Ask for a new one.' }); return }
    checkResetLink(token).then(r => {
      if (!alive) return
      if (r.success && r.valid) setLink({ checking: false, valid: true, error: null })
      else setLink({ checking: false, valid: false, error: r.error })
    })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    if (minPassword && password.length < minPassword) { setError(`Password must be at least ${minPassword} characters long`); return }
    if (password !== confirm) { setError('Passwords do not match'); return }
    setLoading(true)
    const r = await resetPassword(token, password)
    setLoading(false)
    if (r.success) {
      setSessionNotice(r.message)
      navigate('/login', { replace: true })
    } else if (/^RESET_LINK_/.test(r.code || '')) {
      setLink({ checking: false, valid: false, error: r.error })
    } else {
      setError(r.error)
    }
  }

  if (link.checking) {
    return <AuthLayout title="Choose a new password"><LoadingSpinner className="py-6" /></AuthLayout>
  }

  if (!link.valid) {
    return (
      <AuthLayout title="Link not valid">
        <ErrorNote>{link.error}</ErrorNote>
        <div className="space-y-3 text-center text-sm">
          <p><Link to="/forgot-password" className={LINK}>Ask for a new link</Link></p>
          <p><Link to="/login/whatsapp" className={LINK}>Sign in with a WhatsApp code instead</Link></p>
          <p><Link to="/login" className={LINK}>Back to sign in</Link></p>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Choose a new password" subtitle="You will be signed out on your other devices">
      <ErrorNote>{error}</ErrorNote>
      <form onSubmit={handleSubmit} className="space-y-4" aria-label="Choose a new password">
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">New password</label>
          <input id="password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)}
            className={INPUT} placeholder={minPassword ? `At least ${minPassword} characters` : 'New password'}
            minLength={minPassword || undefined} required />
        </div>
        <div>
          <label htmlFor="confirm" className="block text-sm font-medium text-slate-700 mb-1">Confirm new password</label>
          <input id="confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            className={INPUT} placeholder="Type it again" required />
        </div>
        <SubmitButton loading={loading} busyText="Saving…">Save new password</SubmitButton>
      </form>
    </AuthLayout>
  )
}
